import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { Shield, AlertTriangle, Activity, Radio, Eye, Clock, Users, Zap, ChevronDown, RefreshCw, Sun, Moon, CheckCircle, Lock, Navigation, MapPin } from 'lucide-react';
import './SafetyDashboard.css';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SafetyDashboard() {
  const { t } = useTranslation();
  const { isDark, toggleTheme } = useTheme();
  const [activeRides, setActiveRides] = useState([]);
  const [events, setEvents] = useState([]);
  const [safetyEvents, setSafetyEvents] = useState([]);
  const [dutyStatuses, setDutyStatuses] = useState([]);
  const [orgFilter, setOrgFilter] = useState('all');
  const [connected, setConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const eventSourceRef = useRef(null);
  const eventsEndRef = useRef(null);

  // Access control state
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  // Check access on mount
  useEffect(() => {
    const checkAccess = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setAccessChecked(true);
        setHasAccess(false);
        return;
      }
      try {
        const res = await fetch(`${API}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          setAccessChecked(true);
          setHasAccess(false);
          return;
        }
        const data = await res.json();
        setUserInfo(data);
        // Super-admin always has access, B2B users need is_safety_enabled
        if (data.is_admin || data.is_safety_enabled) {
          setHasAccess(true);
          // For B2B users, auto-set org filter to their org
          if (!data.is_admin && data.org_id) {
            setOrgFilter(data.org_id);
          }
        } else {
          setHasAccess(false);
        }
      } catch {
        setHasAccess(false);
      } finally {
        setAccessChecked(true);
      }
    };
    checkAccess();
  }, []);

  // Fetch initial data
  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const [ridesRes, eventsRes, safetyRes] = await Promise.all([
        fetch(`${API}/api/orchestrator/safety/active`),
        fetch(`${API}/api/orchestrator/events?count=100`),
        fetch(`${API}/api/orchestrator/events/safety?count=50`),
      ]);
      const rides = await ridesRes.json();
      const evts = await eventsRes.json();
      const safety = await safetyRes.json();

      setActiveRides(rides.active_rides || []);
      setEvents(evts.events || []);
      setSafetyEvents(safety.events || []);

      // Fetch B2B duty statuses
      try {
        const dutyRes = await fetch(`${API}/api/b2b/user/duty-statuses`, { headers });
        if (dutyRes.ok) {
          const dutyData = await dutyRes.json();
          setDutyStatuses(dutyData.statuses || []);
        }
      } catch (e) {
        // B2B duty statuses optional
      }

      setLastUpdate(new Date());
    } catch (err) {
      console.error('Fetch failed:', err);
    }
  }, []);

  // SSE connection — only connect when access is granted
  useEffect(() => {
    if (!hasAccess || !accessChecked) return;

    fetchData();

    let es = null;
    let retryTimer = null;

    const connectSSE = () => {
      const channel = orgFilter !== 'all' ? `voiceride:events:${orgFilter}` : 'voiceride:events';
      const url = `${API}/api/orchestrator/events/stream?channel=${encodeURIComponent(channel)}`;

      es = new EventSource(url);
      eventSourceRef.current = es;

      es.onopen = () => setConnected(true);
      es.onmessage = (e) => {
        try {
          const event = JSON.parse(e.data);
          if (event.type === 'connected') {
            setConnected(true);
            return;
          }
          if (event.error) {
            setConnected(false);
            return;
          }
          setEvents(prev => [event, ...prev].slice(0, 200));
          if (['emergency', 'safety_checkin', 'safety_checkin_ok', 'safety_checkin_missed',
               'ride_monitoring_started', 'ride_monitoring_stopped', 'emergency_resolved'].includes(event.event_type)) {
            setSafetyEvents(prev => [event, ...prev].slice(0, 100));
          }
          setLastUpdate(new Date());
          if (['ride_monitoring_started', 'ride_monitoring_stopped', 'emergency'].includes(event.event_type)) {
            fetchData();
          }
        } catch {}
      };
      es.onerror = () => {
        setConnected(false);
        es.close();
        // Retry after 5s — don't block UI
        retryTimer = setTimeout(connectSSE, 5000);
      };
    };

    connectSSE();

    return () => {
      if (es) es.close();
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [orgFilter, fetchData, hasAccess, accessChecked]);

  // Stats
  const emergencyCount = activeRides.filter(r => r.emergency === 'true' || r.status === 'EMERGENCY').length;
  const warningCount = activeRides.filter(r => parseInt(r.missed_checkins || 0) >= 1 && r.emergency !== 'true').length;
  const activeCount = activeRides.filter(r => r.status === 'ACTIVE' && r.emergency !== 'true' && parseInt(r.missed_checkins || 0) < 1).length;

  // Filtered rides
  const filteredRides = orgFilter === 'all' ? activeRides : activeRides.filter(r => r.org_id === orgFilter);
  const filteredEvents = orgFilter === 'all' ? events : events.filter(e => e.org_id === orgFilter);

  // Unique orgs
  const orgs = [...new Set(activeRides.map(r => r.org_id).filter(Boolean))];

  // B2B users can only see their own org — restrict org filter
  const isAdmin = userInfo?.is_admin;
  const userOrgId = userInfo?.org_id;

  // Loading / Access Gate
  if (!accessChecked) {
    return (
      <div className="safety-dash" data-testid="safety-dashboard-loading">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="safety-dash" data-testid="safety-dashboard-denied">
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          height: '70vh', textAlign: 'center', gap: '20px',
        }}>
          <div style={{
            width: '80px', height: '80px', borderRadius: '50%',
            background: 'rgba(239,68,68,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Lock size={36} style={{ color: '#ef4444' }} />
          </div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#f1f5f9' }}>
            {t('safetyDashboard.accessDenied', 'Access Restricted')}
          </h1>
          <p style={{ fontSize: '16px', color: '#94a3b8', maxWidth: '420px', lineHeight: 1.6 }}>
            {t('safetyDashboard.contactAdmin', 'You do not have permission to access the Safety Console. Please contact your administrator to enable access for your organization.')}
          </p>
          <a href="/dashboard" style={{
            marginTop: '12px', padding: '10px 28px', borderRadius: '8px',
            background: 'rgba(99,102,241,0.8)', color: '#fff', textDecoration: 'none',
            fontWeight: 600, transition: 'background 0.2s',
          }} data-testid="safety-denied-home-link">
            {t('safetyDashboard.backHome', 'Back to Dashboard')}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="safety-dash" data-testid="safety-dashboard">
      {/* Header */}
      <header className="sd-header" data-testid="sd-header">
        <div className="sd-header-left">
          <Shield size={28} />
          <div>
            <h1>{t('safetyDashboard.title')}</h1>
            <p>{t('safetyDashboard.subtitle')}</p>
          </div>
        </div>
        <div className="sd-header-right">
          <span className={`sd-status-dot ${connected ? 'live' : 'offline'}`} />
          <span className="sd-status-text">{connected ? t('safetyDashboard.live') : t('safetyDashboard.offline')}</span>
          {lastUpdate && (
            <span className="sd-last-update">
              <Clock size={12} /> {lastUpdate.toLocaleTimeString()}
            </span>
          )}
          <OrgFilter orgs={orgs} value={orgFilter} onChange={setOrgFilter} isAdmin={isAdmin} userOrgId={userOrgId} />
          <button onClick={toggleTheme} className="sd-theme-btn" data-testid="sd-theme-toggle">
            {isDark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button onClick={fetchData} className="sd-refresh-btn" data-testid="sd-refresh">
            <RefreshCw size={16} />
          </button>
        </div>
      </header>

      {/* Stats Bar */}
      <div className="sd-stats-bar" data-testid="sd-stats-bar">
        <StatCard icon={<Radio />} label={t('safetyDashboard.stats.monitored')} value={activeRides.length} color="blue" />
        <StatCard icon={<Activity />} label={t('safetyDashboard.stats.active')} value={activeCount} color="green" />
        <StatCard icon={<AlertTriangle />} label={t('safetyDashboard.stats.warnings')} value={warningCount} color="yellow" />
        <StatCard icon={<Zap />} label={t('safetyDashboard.stats.emergencies')} value={emergencyCount} color="red" pulse={emergencyCount > 0} />
      </div>

      {/* Emergency Banner */}
      {emergencyCount > 0 && (
        <div className="sd-emergency-banner" data-testid="sd-emergency-banner">
          <Zap size={24} />
          <span>{emergencyCount} {t('safetyDashboard.emergencyBanner')}</span>
        </div>
      )}

      {/* Main Grid */}
      <div className="sd-grid">
        {/* B2B Live Positions Panel */}
        {dutyStatuses.length > 0 && (
          <div className="sd-panel sd-b2b-panel" data-testid="sd-b2b-duty-panel">
            <div className="sd-panel-header">
              <Navigation size={18} />
              <h2>{t('b2b.livePositions', 'Live-Positionen')} ({dutyStatuses.length})</h2>
            </div>
            <div className="sd-b2b-list">
              {dutyStatuses.map(d => (
                <div key={d.user_id} className="sd-b2b-card" data-testid={`sd-b2b-user-${d.user_id}`}>
                  <div className="sd-b2b-card-left">
                    <span className={`sd-b2b-status-dot ${d.status === 'dienstbeginn' ? 'active' : 'online'}`} />
                    <div>
                      <span className="sd-b2b-name">{d.name || d.email}</span>
                      <span className="sd-b2b-role">{d.b2b_role}</span>
                    </div>
                  </div>
                  <div className="sd-b2b-card-right">
                    <span className={`sd-b2b-badge ${d.status}`}>
                      {d.status === 'dienstbeginn' ? t('b2b.dutyActive', 'Im Dienst') : t('b2b.dutyOnline', 'Online')}
                    </span>
                    {d.lat && d.lng && (
                      <span className="sd-b2b-coords">
                        <MapPin size={11} /> {Number(d.lat).toFixed(3)}, {Number(d.lng).toFixed(3)}
                      </span>
                    )}
                    {d.updated_at && (
                      <span className="sd-b2b-time">
                        <Clock size={11} /> {new Date(d.updated_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active Rides Panel */}
        <div className="sd-panel sd-rides-panel" data-testid="sd-rides-panel">
          <div className="sd-panel-header">
            <Eye size={18} />
            <h2>{t('safetyDashboard.panels.monitoredRides')} ({filteredRides.length})</h2>
          </div>
          <div className="sd-rides-list">
            {filteredRides.length === 0 ? (
              <div className="sd-empty">{t('safetyDashboard.noActiveRides')}</div>
            ) : (
              filteredRides.map(ride => (
                <RideCard key={ride.ride_id} ride={ride} onResolve={fetchData} />
              ))
            )}
          </div>
        </div>

        {/* Live Event Feed */}
        <div className="sd-panel sd-events-panel" data-testid="sd-events-panel">
          <div className="sd-panel-header">
            <Activity size={18} />
            <h2>{t('safetyDashboard.panels.liveEventFeed')}</h2>
            <span className="sd-event-count">{filteredEvents.length}</span>
          </div>
          <div className="sd-events-list">
            {filteredEvents.slice(0, 50).map((evt, i) => (
              <EventRow key={`${evt.event_id || evt.stream_id}-${i}`} event={evt} />
            ))}
            <div ref={eventsEndRef} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────

function StatCard({ icon, label, value, color, pulse }) {
  return (
    <div className={`sd-stat ${color} ${pulse ? 'pulse' : ''}`} data-testid={`sd-stat-${color}`}>
      <div className="sd-stat-icon">{icon}</div>
      <div className="sd-stat-value">{value}</div>
      <div className="sd-stat-label">{label}</div>
    </div>
  );
}

function RideCard({ ride, onResolve }) {
  const { t } = useTranslation();
  const [resolving, setResolving] = useState(false);
  const isEmergency = ride.emergency === 'true' || ride.status === 'EMERGENCY';
  const missed = parseInt(ride.missed_checkins || 0);
  const isWarning = missed >= 1 && !isEmergency;
  const statusClass = isEmergency ? 'emergency' : isWarning ? 'warning' : 'active';

  let intervals = ride.checkin_intervals;
  if (typeof intervals === 'string') {
    try { intervals = JSON.parse(intervals); } catch { intervals = []; }
  }

  const handleResolve = async () => {
    setResolving(true);
    try {
      const res = await fetch(`${API}/api/orchestrator/safety/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ride_id: ride.ride_id, resolved_by: 'dashboard_admin' }),
      });
      if (res.ok) {
        if (onResolve) onResolve();
      }
    } catch (err) {
      console.error('Resolve failed:', err);
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className={`sd-ride-card ${statusClass}`} data-testid={`sd-ride-${ride.ride_id}`}>
      <div className="sd-ride-header">
        <span className={`sd-ride-status ${statusClass}`}>
          {isEmergency ? t('safetyDashboard.rideCard.emergency') : isWarning ? t('safetyDashboard.rideCard.warning') : t('safetyDashboard.rideCard.active')}
        </span>
        <span className="sd-ride-id">#{ride.ride_id?.slice(0, 8)}</span>
      </div>
      <div className="sd-ride-details">
        <div className="sd-ride-row">
          <Users size={14} />
          <span>{t('safetyDashboard.rideCard.passenger')}: {ride.user_id?.slice(0, 12)}</span>
        </div>
        {ride.driver_id && (
          <div className="sd-ride-row">
            <Activity size={14} />
            <span>{t('safetyDashboard.rideCard.driver')}: {ride.driver_id?.slice(0, 12)}</span>
          </div>
        )}
        {ride.org_id && (
          <div className="sd-ride-row">
            <Shield size={14} />
            <span>{t('safetyDashboard.rideCard.org')}: {ride.org_id}</span>
          </div>
        )}
        <div className="sd-ride-row">
          <Clock size={14} />
          <span>{t('safetyDashboard.rideCard.since')}: {ride.started_at ? new Date(ride.started_at).toLocaleTimeString() : '—'}</span>
        </div>
        {Array.isArray(intervals) && intervals.length > 0 && (
          <div className="sd-ride-row">
            <Radio size={14} />
            <span>{t('safetyDashboard.rideCard.checkins')}: {intervals.join(', ')} min</span>
          </div>
        )}
        {missed > 0 && (
          <div className="sd-ride-row sd-missed">
            <AlertTriangle size={14} />
            <span>{t('safetyDashboard.rideCard.missedCheckins')}: {missed}</span>
          </div>
        )}
      </div>
      {isEmergency && (
        <button
          className="sd-resolve-btn"
          onClick={handleResolve}
          disabled={resolving}
          data-testid={`sd-resolve-${ride.ride_id}`}
        >
          <CheckCircle size={14} />
          {resolving ? t('safetyDashboard.rideCard.resolving') : t('safetyDashboard.rideCard.resolve')}
        </button>
      )}
    </div>
  );
}

function EventRow({ event }) {
  const { t } = useTranslation();
  const type = event.event_type || 'unknown';
  const severity = event.severity || 'info';

  const severityClass = severity === 'critical' ? 'critical' : severity === 'warning' ? 'warn' : 'info';

  const iconMap = {
    emergency: <Zap size={14} />,
    emergency_resolved: <Shield size={14} />,
    safety_checkin: <Radio size={14} />,
    safety_checkin_ok: <Activity size={14} />,
    safety_checkin_missed: <AlertTriangle size={14} />,
    ride_monitoring_started: <Eye size={14} />,
    ride_monitoring_stopped: <Clock size={14} />,
    dialog_turn: <Activity size={14} />,
    ride_search_executed: <Activity size={14} />,
  };

  const eventLabelMap = {
    emergency: t('safetyDashboard.events.emergency'),
    emergency_resolved: t('safetyDashboard.events.emergencyResolved'),
    safety_checkin: t('safetyDashboard.events.safetyCheckin'),
    safety_checkin_ok: t('safetyDashboard.events.safetyCheckinOk'),
    safety_checkin_missed: t('safetyDashboard.events.safetyCheckinMissed'),
    ride_monitoring_started: t('safetyDashboard.events.rideMonitoringStarted'),
    ride_monitoring_stopped: t('safetyDashboard.events.rideMonitoringStopped'),
    dialog_turn: t('safetyDashboard.events.dialogTurn'),
    ride_search_executed: t('safetyDashboard.events.rideSearchExecuted'),
    state_reset: t('safetyDashboard.events.stateReset'),
  };

  const ts = event.timestamp ? new Date(event.timestamp).toLocaleTimeString() : '';

  return (
    <div className={`sd-event-row ${severityClass}`} data-testid={`sd-event-${type}`}>
      <span className="sd-event-icon">{iconMap[type] || <Activity size={14} />}</span>
      <span className="sd-event-type">{eventLabelMap[type] || type.replace(/_/g, ' ')}</span>
      {event.user_id && <span className="sd-event-user">{event.user_id.slice(0, 10)}</span>}
      {event.org_id && <span className="sd-event-org">{event.org_id}</span>}
      <span className="sd-event-time">{ts}</span>
    </div>
  );
}

function OrgFilter({ orgs, value, onChange, isAdmin, userOrgId }) {
  const { t } = useTranslation();

  // B2B users can only see their own org — no filter control
  if (!isAdmin && userOrgId) {
    return (
      <div className="sd-org-filter" data-testid="sd-org-filter">
        <span style={{ fontSize: '13px', color: '#a5b4fc', padding: '4px 10px' }}>{userOrgId}</span>
      </div>
    );
  }

  return (
    <div className="sd-org-filter" data-testid="sd-org-filter">
      <select value={value} onChange={e => onChange(e.target.value)}>
        <option value="all">{t('safetyDashboard.allOrgs')}</option>
        {orgs.map(org => (
          <option key={org} value={org}>{org}</option>
        ))}
      </select>
      <ChevronDown size={14} className="sd-select-icon" />
    </div>
  );
}
